// Системные экраны вне стека (ux/11 §3): BOOT, UNSUPPORTED, UPDATE-REQUIRED, MAINTENANCE, BANNED, NO-NETWORK, FATAL.
// Все: навигация и BackButton скрыты, SettingsButton видна (кроме BANNED). Фон — DOM + CSS, без PixiJS.
import type { ComponentType } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { CLIENT_BUILD, useApp } from '../app/app.js';
import { player } from '../state/game.js';
import { bootUi, MAINT_RETRY_MS } from '../boot/boot.js';
import { duration, mskTime } from '../i18n/format.js';
import type { SystemId } from '../nav/navigator.js';
import { TutLetter } from '../tutorial/letter.js';
import { useNow } from '../ui/kit.js';
import { I } from '../assets/icons.js';
import { t as tr } from '../i18n/t.js';

type P = { params: Record<string, unknown> };
/** Перезапуск входа — ставит main.tsx (там есть initData и Telegram). */
export const reboot = { run: () => {} };
/** Прямая ссылка Mini App; на стенде — тестовый бот, в проде задаётся VITE_TG_APP_LINK при сборке. */
export const GAME_LINK: string = import.meta.env.VITE_TG_APP_LINK || 'https://t.me/garages_test_bot';
export const CHANNEL_LINK = 'https://t.me/garages_game';

const JOKES = [tr('CL-SCREENS-SYSTEM-01'), tr('UI-LOADING-3'), tr('CL-SCREENS-SYSTEM-02'), tr('CL-SCREENS-SYSTEM-03'), tr('CL-SCREENS-SYSTEM-04')];

function Nut(p: { progress: number }) {
  return (
    <div class="shelf" aria-hidden>
      <i style={{ width: `${Math.round(p.progress * 100)}%` }} />
      <svg class="nut" viewBox="0 0 20 20" style={{ left: `calc(${Math.round(p.progress * 100)}% - 10px)` }}>
        <polygon points="10,1 18,5.5 18,14.5 10,19 2,14.5 2,5.5" fill="#c9c0b4" stroke="#1f1408" stroke-width="1.5" />
        <circle cx="10" cy="10" r="3.5" fill="#2a2620" />
      </svg>
    </div>
  );
}

export function BootScreen(p: P) {
  const app = useApp();
  const ui = bootUi.value;
  const [joke, setJoke] = useState(0);
  useEffect(() => { const t = setInterval(() => setJoke((j) => (j + 1) % JOKES.length), 2000); return () => clearInterval(t); }, []);
  const code = p.params['code'] as string | undefined;
  if (code) {
    const reopen = code === 'INIT_DATA_INVALID' || code === 'INIT_DATA_EXPIRED' || code === 'REFRESH_REUSED' || code === 'SIGNATURE_INVALID';
    return (
      <div class="system" data-testid="SCR-BOOT" data-state="error">
        <div class="logo">{tr('CL-SCREENS-SYSTEM-05')}</div>
        <h1>{reopen ? tr('CL-SCREENS-SYSTEM-06') : tr('CL-SCREENS-SYSTEM-07')}</h1>
        <p>{(p.params['text'] as string) ?? tr('CL-SCREENS-SYSTEM-08')}</p>
        {reopen
          ? <button class="btn accent" onClick={() => app.wa?.openTelegramLink?.(GAME_LINK)}>{tr('CL-SCREENS-SYSTEM-09')}</button>
          : <button class="btn accent" onClick={() => reboot.run()}>{tr('CL-SCREENS-SYSTEM-10')}</button>}
        <span class="code">{code}</span>
      </div>
    );
  }
  return (
    <div class="system" data-testid="SCR-BOOT" aria-busy="true">
      <div class="logo">{tr('CL-SCREENS-SYSTEM-05')}</div>
      <Nut progress={ui.progress} />
      <div class="joke" aria-live="polite">{ui.stuck ? tr('CL-SCREENS-SYSTEM-11') : ui.slow ? tr('CL-SCREENS-SYSTEM-12') : JOKES[joke]}</div>
      {ui.stuck ? <button class="btn accent" data-testid="boot-retry" onClick={() => location.reload()}>{tr('CL-SCREENS-SYSTEM-10')}</button> : null}
    </div>
  );
}

export function UnsupportedScreen(p: P) {
  const app = useApp();
  const tg = p.params['reason'] === 'telegram';
  return (
    <div class="system" data-testid="SCR-UNSUPPORTED">
      <I n="noTg" size={64} class="ill" />
      <h1>{tg ? tr('CL-SCREENS-SYSTEM-13') : tr('CL-SCREENS-SYSTEM-14')}</h1>
      <p>{tg ? tr('CL-SCREENS-SYSTEM-15') : tr('CL-SCREENS-SYSTEM-16')}</p>
      <button class="btn accent" onClick={() => app.wa?.openLink?.(tg ? 'https://telegram.org/apps' : location.href)}>{tg ? tr('CL-SCREENS-SYSTEM-17') : tr('CL-SCREENS-SYSTEM-18')}</button>
    </div>
  );
}

/** Адрес перезапуска без кэша: метка версии в адресе (сервер прислал latestBuild — она, иначе время). */
export function reloadUrl(href: string, latest: string | undefined, now = Date.now()): string {
  const u = new URL(href);
  u.searchParams.set('v', latest ?? String(now));
  return u.href;
}

/**
 * SCR-UPDATE-REQUIRED (E02-T07, ux/11 §3.3): на 426 CLIENT_TOO_OLD с любого запроса или minBuild из /meta.
 * «Перезапустить» — перезагрузка в обход кэша с меткой версии; после неё сборка совпадает с latestBuild.
 */
export function UpdateRequiredScreen(p: P) {
  const d = (p.params['details'] ?? {}) as { minBuild?: string; latestBuild?: string };
  return (
    <div class="system" data-testid="SCR-UPDATE-REQUIRED">
      <I n="wrenchBig" size={64} class="ill" />
      <h1>{tr('CL-SCREENS-SYSTEM-19')}</h1>
      <p>{tr('ERR-VERSION')}</p>
      <p class="small" data-testid="update-versions">{tr('CL-SYS-VERSION', { v: CLIENT_BUILD })}{d.latestBuild ?? d.minBuild ? ` · ${tr('CL-SYS-VERSION-NEED', { v: d.latestBuild ?? d.minBuild })}` : ''}</p>
      <button class="btn accent" data-testid="update-reload" onClick={() => location.replace(reloadUrl(location.href, d.latestBuild))}>{tr('CL-SCREENS-SYSTEM-20')}</button>
    </div>
  );
}

/** «Вернёмся к 15:00 МСК (через 42 мин)», отсчёт; по окончании — повтор входа каждые 30 с (ux/11 §3.4). */
export function MaintenanceScreen(p: P) {
  const app = useApp();
  const to = Number(p.params['to']) || Date.now() + 60_000;
  const now = useNow();
  const left = to - now;
  useEffect(() => {
    if (left > 0) return;
    reboot.run();
    const t = setInterval(() => reboot.run(), MAINT_RETRY_MS);
    return () => clearInterval(t);
  }, [left <= 0]);
  return (
    <div class="system" data-testid="SCR-MAINTENANCE">
      <I n="maint" size={64} class="ill" />
      <h1>{tr('CL-SCREENS-SYSTEM-21')}</h1>
      <p>{tr('CL-SCREENS-SYSTEM-22')}{" "}{mskTime(to)} {" "}{tr('CL-SCREENS-SYSTEM-23')}{left > 0 ? tr('CL-SCREENS-SYSTEM-24', { p0: duration(left) }) : ''}.</p>
      <p class="small" data-testid="maint-state">{left > 0 ? tr('CL-SCREENS-SYSTEM-25') : tr('CL-SCREENS-SYSTEM-26')}</p>
      <button class="btn outline" onClick={() => app.wa?.openTelegramLink?.(CHANNEL_LINK)}>{tr('CL-SCREENS-SYSTEM-27')}</button>
    </div>
  );
}

/** «Оспорить» — бот поддержки с ID игрока (E02-T07): `start=appeal_<publicId>`. */
export function appealLink(publicId: string | undefined): string {
  return `https://t.me/garages_support_bot${publicId ? `?start=appeal_${publicId}` : ''}`;
}

/**
 * SCR-BANNED (E02-T07, ux/11 §3.5): срок «до {дата}» или «навсегда» и категория одной строкой из ответа сервера;
 * никаких игровых данных. ID игрока — из ответа, иначе из сохранённого профиля.
 */
export function BannedScreen(p: P) {
  const app = useApp();
  const d = (p.params['details'] ?? {}) as { until?: string | null; reason?: string; category?: string; publicId?: string };
  const cat = d.category ?? d.reason;
  const id = d.publicId ?? player.value?.publicId;
  return (
    <div class="system" data-testid="SCR-BANNED">
      <I n="lock" size={64} class="ill" />
      <h1>{tr('CL-SCREENS-SYSTEM-28')}{" "}{d.until ? tr('CL-SCREENS-SYSTEM-29', { p0: new Date(d.until).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) }) : tr('CL-SCREENS-SYSTEM-30')}</h1>
      {cat ? <p data-testid="ban-category">{tr(banCategoryKey(cat))}</p> : null}
      {id ? <p class="small muted" data-testid="ban-id">{tr('CL-BAN-ID', { id })}</p> : null}
      <button class="btn outline" data-testid="ban-appeal" onClick={() => app.wa?.openTelegramLink?.(appealLink(id))}>{tr('CL-SCREENS-SYSTEM-32')}</button>
    </div>
  );
}

/**
 * Категория бана — `reason_code` санкции админки (Р-95: 'AC-MULTI', 'AC-DMG-ANOMALY', 'CHAT-INSULT', 'PAY-ABUSE', …):
 * одна строка без деталей расследования. Точное совпадение — из таблицы, иначе по префиксу.
 */
export function banCategoryKey(code: string): string {
  if (BAN_CATEGORY[code]) return BAN_CATEGORY[code]!;
  if (code === 'AC-MULTI') return 'CL-BAN-MULTIACC';
  if (code.startsWith('AC-')) return 'CL-BAN-CHEATING';
  if (code.startsWith('CHAT-')) return 'CL-BAN-ABUSE';
  if (code.startsWith('PAY-')) return 'CL-BAN-PAYMENTS';
  if (code.startsWith('SPAM')) return 'CL-BAN-SPAM';
  return 'CL-BAN-OTHER';
}

/** Категории бана (ГД 14, модерация): одна строка без деталей расследования. */
const BAN_CATEGORY: Record<string, string> = {
  cheating: 'CL-BAN-CHEATING', fraud: 'CL-BAN-FRAUD', payments: 'CL-BAN-PAYMENTS', abuse: 'CL-BAN-ABUSE', multiacc: 'CL-BAN-MULTIACC', spam: 'CL-BAN-SPAM',
};

/** Нет сети и нет кэша: «Повторить» и автоматическая попытка по событию online (ux/11 §3.6). */
export function NoNetworkScreen() {
  useEffect(() => {
    const on = () => reboot.run();
    addEventListener('online', on);
    return () => removeEventListener('online', on);
  }, []);
  return (
    <div class="system" data-testid="SCR-NO-NETWORK">
      <I n="noNet" size={64} class="ill" />
      <h1>{tr('CL-SCREENS-SYSTEM-33')}</h1>
      <p>{tr('ERR-NET')}</p>
      <button class="btn accent" onClick={() => reboot.run()}>{tr('CL-SCREENS-SYSTEM-10')}</button>
    </div>
  );
}

export function FatalScreen(p: P) {
  const app = useApp();
  return (
    <div class="system" data-testid="SCR-FATAL">
      <I n="fatal" size={64} class="ill" />
      <h1>{tr('CL-SCREENS-SYSTEM-34')}</h1>
      <p>{tr('CL-SCREENS-SYSTEM-35')}</p>
      <button class="btn accent" onClick={() => location.reload()}>{tr('CL-SCREENS-SYSTEM-20')}</button>
      <button class="btn outline" onClick={() => app.wa?.openTelegramLink?.('https://t.me/garages_support_bot')}>{tr('CL-SCREENS-SYSTEM-36')}</button>
      <span class="code" data-testid="fatal-code">{tr('CL-SCREENS-SYSTEM-37')}{" "}{String(p.params['code'] ?? '—')}</span>
    </div>
  );
}

export const SYSTEM_SCREENS: Record<SystemId, ComponentType<P>> = {
  'SCR-BOOT': BootScreen,
  'SCR-UNSUPPORTED': UnsupportedScreen,
  'SCR-UPDATE-REQUIRED': UpdateRequiredScreen,
  'SCR-MAINTENANCE': MaintenanceScreen,
  'SCR-BANNED': BannedScreen,
  'SCR-NO-NETWORK': NoNetworkScreen,
  'SCR-FATAL': FatalScreen,
  'SCR-TUT-LETTER': TutLetter,
};
